import { Component, Element, Prop, h } from '@stencil/core';

import bannerActions from '../actions/actions.interactive-banner';
import { state as bannerState, on as onBannerStateChange } from '../store/store.interactive-banner';
import { on as onValidationStateChange, state as validationState } from '../store/store.validation';
import { BannerInstallationMode, BannerStep } from '../utils/utils.interactive-banner';

@Component({
  tag: 'interactive-banner-hydrator',
  shadow: true,
})
export class InteractiveBannerHydrator {
  @Element() el: HTMLElement;

  @Prop() offerCode: string;
  @Prop() dealerId: string;
  @Prop() groupId: string;
  @Prop() dealerGroupId: string;
  @Prop() voiVin: string;
  @Prop() installationMode: BannerInstallationMode;
  @Prop() tabList: string = 'vin-lp-ymm';
  @Prop() defaultSelectedTab: string = 'vin';
  @Prop() zIndex: number = 2000;
  @Prop() vin: string;
  @Prop() mileage: number;

  async componentWillLoad() {
    // boostrap props
    bannerActions.onReceivedParams({
      offerCode: this.offerCode,
      dealerId: this.dealerId,
      dealerGroupId: this.dealerGroupId,
      groupId: this.groupId,
      voiVin: this.voiVin,
      installationMode: this.installationMode,
      tabList: this.tabList,
      defaultSelectedTab: this.defaultSelectedTab,
      zIndex: this.zIndex,
      vin: this.vin,
      mileage: this.mileage,
    });

    bannerActions.onOptimizelyCheck();
    bannerActions.onHealthCheck();
    bannerActions.checkSessionStorageToContinue();
    await bannerActions.onCheckInit(this.groupId, this.dealerId, this.dealerGroupId);

    const continueAdnUnsubcribedSteps = [BannerStep.UnSubscribed, BannerStep.Continue, BannerStep.Eligible, BannerStep.Review, BannerStep.Inspection];
    if (!continueAdnUnsubcribedSteps.includes(bannerState.activeStep) && this.vin) {
      bannerActions.onFindByVin(this.vin);
    }

    this.setupStateProps();

    onValidationStateChange('set', (prop, value) => {
      if (prop === 'form') {
        this.updateStateProps('errors', new Set<string>(value.banner));
      }
    });

    onBannerStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onBannerStateChange('reset', () => {
      this.setupStateProps();
    });
  }

  componentDidLoad() {
    window.addEventListener('message', bannerActions.onMessage);
  }

  componentWillUpdate() {
    bannerActions.onReceivedParams({
      offerCode: this.offerCode,
      dealerId: this.dealerId,
      dealerGroupId: this.dealerGroupId,
      groupId: this.groupId,
      voiVin: this.voiVin,
      installationMode: this.installationMode,
      tabList: this.tabList,
      defaultSelectedTab: this.defaultSelectedTab,
      zIndex: this.zIndex,
      vin: this.vin,
      mileage: this.mileage,
    });
  }

  disconnectedCallback() {
    bannerActions.onBannerDisconnected();
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    this.el.childNodes.forEach((element) => {
      element[prop] = value;
    });
  }

  // explicitly declare initial state props
  setupStateProps() {
    this.el.childNodes.forEach((element) => {
      element['errors'] = validationState.form.banner;

      element['offerCode'] = this.offerCode;
      element['dealerId'] = this.dealerId;
      element['dealerGroupId'] = this.dealerGroupId;
      element['groupId'] = this.groupId;
      element['voiVin'] = this.voiVin;
      element['installationMode'] = this.installationMode;
      element['tabList'] = this.tabList;
      element['tabHeaders'] = bannerState.tabHeaders;
      element['defaultSelectedTab'] = this.defaultSelectedTab;
      element['zIndex'] = this.zIndex;
      element['states'] = bannerState.states;
      element['isFetching'] = bannerState.isFetching;
      element['activeStep'] = bannerState.activeStep;
      element['vehiclePossibilities'] = bannerState.vehiclePossibilities;
      element['furyUrl'] = bannerState.furyUrl;
      element['optimizelyBannerIsActive'] = bannerState.optimizelyBannerIsActive;
      element['isServicesHealthy'] = bannerState.isServicesHealthy;
      element['isOpenModal'] = bannerState.isOpenModal;
      element['ymmSuggestions'] = bannerState.ymmSuggestions;
      element['year'] = bannerState.year;
      element['yearId'] = bannerState.yearId;
      element['make'] = bannerState.make;
      element['makeId'] = bannerState.makeId;
      element['model'] = bannerState.model;
      element['modelId'] = bannerState.modelId;
      element['trim'] = bannerState.trim;
      element['trimId'] = bannerState.trimId;
      element['color'] = bannerState.color;
      element['colorId'] = bannerState.colorId;
      element['transmission'] = bannerState.transmission;
      element['transmissionId'] = bannerState.transmissionId;
      element['engine'] = bannerState.engine;
      element['engineId'] = bannerState.engineId;
      element['drivetrain'] = bannerState.drivetrain;
      element['drivetrainId'] = bannerState.drivetrainId;
      element['vin'] = bannerState.vin;
      element['mileage'] = bannerState.mileage;
      element['inputVin'] = bannerState.inputVin;
      element['licensePlateNumber'] = bannerState.licensePlateNumber;
      element['licensePlateState'] = bannerState.licensePlateState;
      element['inputYmm'] = bannerState.inputYmm;
      element['vehicleId'] = bannerState.vehicleId;
      element['currentSelectedTab'] = bannerState.currentSelectedTab;
      element['amount'] = bannerState.amount;
      element['markupAmounts'] = bannerState.markupAmounts;
      element['totalOfferAmounts'] = bannerState.totalOfferAmounts;
      element['expirationDate'] = bannerState.expirationDate;
      element['isActiveListing'] = bannerState.isActiveListing;
      element['activeDealerListingUrl'] = bannerState.activeDealerListingUrl;
      element['sdContainer'] = bannerState.sdContainer;
      element['sdOemDealerCode'] = bannerState.oemDealerCode;
    });
  }

  render() {
    return (
      <div class="hydration-state hydration-state--banner">
        <slot />
      </div>
    );
  }
}
